import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { Presentation, FlaskConical, Puzzle, MessagesSquare, ClipboardList, Layers, Cloud, BadgeCheck, Sparkles, Cog, CheckCircle2 } from "lucide-react";

const B = "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/";
const KF_PHOTOS = {
  distillation: B + "mshg4mb2_Screenshot%202026-01-31%20155637.png",
  microscope:   B + "2cztet2t_Screenshot%202026-01-31%20155353.png",
  pipette:      B + "pxzaux2x_Screenshot%202026-01-31%20153409.png",
};

const FEATURES = [
  {
    icon: Presentation,
    hex: "#2E6BB0",
    tag: "01",
    t: "Demonstration-Based Learning",
    lead: "Replacing traditional chalk-and-talk with live demonstrations.",
    d: "PBL-TDLE transforms conventional classrooms into advanced Concept Labs where every concept is taught through live demonstrations using high-end, global-standard scientific apparatus. Instead of imagining concepts through lectures alone, students directly observe scientific principles in action — making learning visual, interactive and concept-focused.",
  },
  {
    icon: FlaskConical,
    hex: "#1E9E8A",
    tag: "02",
    t: "Investigation & Experimental Verification",
    lead: "Learning is not assumed. It is verified.",
    d: "Students investigate and validate every concept by performing experiments themselves after teacher demonstrations. Through observation, measurements, analysis, graphing and interpretation of results, they achieve deep conceptual understanding and long-term mastery.",
  },
  {
    icon: Puzzle,
    hex: "#F0A831",
    tag: "03",
    t: "Immediate Application — Real-Life Open-Ended Problems",
    lead: "Concepts are applied the moment they are learned.",
    d: "Concepts are immediately applied to real-life and open-ended problems through the IDEA Pedagogy framework — Identify · Develop · Execute · Assess. Students learn how multiple concepts work together to solve complex situations, developing analytical thinking, innovation, creativity and advanced problem-solving ability.",
  },
  {
    icon: MessagesSquare,
    hex: "#8B5CF6",
    tag: "04",
    t: "Guided Practice Problem Solving in the Classroom",
    lead: "Immediate guidance. Immediate correction.",
    d: "Students regularly solve practice problems in the presence of teachers — enabling immediate discussion, correction and doubt clearing. This continuous problem-solving environment strengthens concept application skills and builds confidence for national and international competitive examinations.",
  },
  {
    icon: ClipboardList,
    hex: "#E14C7A",
    tag: "05",
    t: "Problem-Solution Presentations",
    lead: "Learning that continues beyond the classroom.",
    d: "Structured problem-solving home assignments let students tackle challenging problems and then present their solutions to teachers and peers. This develops communication skills, logical thinking, presentation confidence and deeper conceptual clarity.",
  },
  {
    icon: Layers,
    hex: "#2E6BB0",
    tag: "06",
    t: "IDEA Pedagogy — A New Classroom Model for Problem Solving",
    lead: "Identify · Develop · Execute · Assess.",
    d: "IDEA Pedagogy ensures students identify problems, develop concepts through demonstrations and experiments, apply them in problem solving, and continuously assess and improve their understanding. It develops conceptual mastery, scientific thinking, innovation, analytical ability, presentation, leadership and real-life application ability.",
  },
  {
    icon: Cloud,
    hex: "#1E9E8A",
    tag: "07",
    t: "CoPE-App · Cloud-Based Academic Engine",
    lead: "AI-powered, data-driven and tailor-made per class.",
    d: "The CoPE-App is a comprehensive AI-powered platform serving as the academic engine of PBL-TDLE. It provides AI-based cohort diagnostics, curriculum-mapped concepts, curated problems, experiments, apparatus specs, real-time formative assessment and daily dashboards for teachers, students, parents and school leaders.",
  },
  {
    icon: BadgeCheck,
    hex: "#F0A831",
    tag: "08",
    t: "Measurable & Verifiable Learning",
    lead: "Assessed and corrected the moment it happens.",
    d: "Demonstrations and experiments create visible evidence of learning — observations, measurements, calculations, graphs, conclusions, presentations. Teachers can identify gaps immediately, provide support and verify improvement before moving forward. Learning becomes measurable, correctable and verifiable in real time.",
  },
  {
    icon: Sparkles,
    hex: "#8B5CF6",
    tag: "09",
    t: "Student Engagement — The Foundation of Deep Learning",
    lead: "Every mind, active. Every day.",
    d: "Backed by the science of Flow (Csikszentmihalyi), the Zone of Proximal Development (Vygotsky) and deliberate practice (Gladwell), PBL-TDLE keeps every student intellectually engaged — investigating, discussing, applying and improving — turning effort into ability.",
  },
  {
    icon: Cog,
    hex: "#E14C7A",
    tag: "10",
    t: "Teacher-Independent · System-Driven Learning",
    lead: "Standardised excellence, not teacher-dependent luck.",
    d: "In conventional systems, learning quality depends heavily on an individual teacher's expertise, mood and energy. PBL-TDLE, supported by the CoPE-App, transforms education into a standardised, guided and system-driven model — ensuring every classroom delivers the same rigour of concept development and problem solving.",
  },
];

const IDEA_STEPS = [
  { l: "I", t: "Identify the Problem",   d: "Diagnose the situation, unpack given and required information." },
  { l: "D", t: "Develop the Concepts",   d: "Through teacher demonstrations + student experiments." },
  { l: "E", t: "Execute the Solution",   d: "Apply concepts, plan and solve — with reasoning at every step." },
  { l: "A", t: "Assess the Solution",    d: "Evaluate, verify, correct, present and improve." },
];

const IDEA_OUTCOMES = [
  "Conceptual Mastery",
  "Scientific Thinking",
  "Problem-Solving Skills",
  "Innovation & Creativity",
  "Analytical Ability",
  "Communication & Presentation",
  "Confidence & Leadership",
  "Experimental & Research Skills",
  "Real-Life Application Ability",
];

export default function KeyFeatures() {
  return (
    <div data-testid="key-features-page">
      <FloatingQuote />
      <PageHero
        n="F"
        label="Key Features & Model"
        lines={["Ten features that make", "PBL-TDLE different."]}
        intro="From demonstration-based teaching and experimental verification to an AI-powered academic engine and a system-driven learning model — the ten mechanics that turn classrooms into Concept Labs."
      />

      {/* Ten features */}
      <section className="border-t border-white/10" data-testid="kf-features">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">The Ten Features of PBL-TDLE</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Every feature designed to make learning visible, verified and applied.
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.tag} delay={(i % 2) * 0.06} className="border border-white/10 rounded-2xl p-8 bg-[#050B14] flex flex-col hover:border-white/25 transition-colors" data-testid={`kf-feature-${f.tag}`}>
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center border shrink-0" style={{ borderColor: f.hex, backgroundColor: `${f.hex}22` }}>
                      <Icon className="w-7 h-7" style={{ color: f.hex }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-mono-label text-[11px] tracking-label uppercase" style={{ color: f.hex }}>Feature {f.tag}</div>
                      <h3 className="font-display text-2xl text-white mt-2 tracking-tight leading-tight">{f.t}</h3>
                      <p className="text-white/85 mt-2 text-sm italic">{f.lead}</p>
                    </div>
                  </div>
                  <p className="text-white/65 leading-relaxed mt-6 flex-1">{f.d}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* IDEA deep-dive */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="kf-idea">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-5">
            <SectionLabel n="B">The IDEA Framework</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Four integrated stages — turning problems into concepts, and concepts into solutions.
            </h2>
            <div className="mt-10 space-y-4">
              {IDEA_STEPS.map((s) => (
                <div key={s.l} className="flex items-start gap-4 border border-white/10 rounded-xl p-5">
                  <span className="w-10 h-10 rounded-full border border-accent text-accent flex items-center justify-center font-display text-xl shrink-0">{s.l}</span>
                  <div>
                    <div className="font-display text-lg text-white leading-snug">{s.t}</div>
                    <p className="text-white/60 leading-relaxed mt-1 text-sm">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-7">
            <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-white/10">
              <img src={KF_PHOTOS.pipette} alt="Students applying IDEA — hands-on chemistry investigation" className="w-full h-full object-cover" />
            </div>
            <div className="mt-6 border border-white/10 rounded-2xl p-6">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">What IDEA Develops in Every Student</div>
              <div className="grid sm:grid-cols-2 gap-2 mt-4">
                {IDEA_OUTCOMES.map((o) => (
                  <div key={o} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-c-teal shrink-0 mt-0.5" /> {o}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Closing pull-quote */}
      <section className="border-t border-white/10 bg-cream text-ink" data-testid="kf-closing">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-7">
            <SectionLabel n="✦" dark>The Model, In One Line</SectionLabel>
            <blockquote className="font-display text-3xl md:text-5xl text-ink leading-[1.15] tracking-tight mt-10 font-light">
              “From <span className="text-[#1B4A7A]">content delivery</span> to <span className="text-[#1B4A7A]">concept mastery</span> — one demonstration, one experiment, one problem at a time.”
            </blockquote>
          </Reveal>
          <Reveal delay={0.08} className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-ink/10">
              <img src={KF_PHOTOS.microscope} alt="Students investigating under the microscope" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
